import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { updateCsvImportLogConfirmationAction, softDeleteCsvImportLogAction } from '../../redux/actions/csvImportLogs/csvImportLogsAction';
import { confirmDelete } from '../../utils/confirmDelete';
import { deleteImportedInventoryByReference } from '../../redux/actions/importedInventory/importedInventoryAction';

const CsvImportLogsTable = ({ logs, loading, onRefresh, currentUser }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.login);

  const handleViewBatch = (referenceNumber) => {
    navigate(`/csv-import-batch/${referenceNumber}`);
  };

  const handleConfirm = async (id, currentStatus) => {
    // Ask for confirmation before changing status
    const result = await confirmDelete({
      title: currentStatus ? 'Unconfirm Batch?' : 'Confirm Batch?',
      text: currentStatus
        ? 'Are you sure you want to unconfirm this batch?'
        : 'Are you sure you want to confirm this batch?',
      confirmButtonText: currentStatus ? 'Yes, Unconfirm' : 'Yes, Confirm',
      cancelButtonText: 'Cancel'
    });
    if (!result.isConfirmed) return;
    try {
      console.log('Confirming log:', { id, currentStatus, newStatus: !currentStatus });
      const res = await dispatch(updateCsvImportLogConfirmationAction(id, !currentStatus));
      console.log('Confirmation result:', res);
      toast.success(`Import batch ${currentStatus ? 'unconfirmed' : 'confirmed'} successfully`);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update confirmation status';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete();
    if (result.isConfirmed) {
      try {
        await dispatch(softDeleteCsvImportLogAction(id));
        toast.success('Import log deleted successfully');
        if (onRefresh) {
          onRefresh();
        }
      } catch (error) {
        // Show specific error if batch data exists
        if (error.response?.data?.message === 'Remove the batch data first and try to delete this log data.') {
          toast.error('Remove the batch data first and try to delete this log data.');
        } else {
          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete import log';
          toast.error(errorMessage);
        }
      }
    }
  };

  const columns = [
    {
      name: 'Reference Number',
      selector: row => row.reference_number,
      sortable: true,
      width: '150px',
      cell: row => (
        <Box sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {row.reference_number}
        </Box>
      )
    },
    {
      name: 'Import Date',
      selector: row => row.imported_date_time,
      sortable: true,
      width: '180px',
      cell: row => (
        <Box>
          {new Date(row.imported_date_time).toLocaleDateString()}
          <br />
          <small style={{ color: '#666' }}>
            {new Date(row.imported_date_time).toLocaleTimeString()}
          </small>
        </Box>
      )
    },
    {
      name: 'Total Records',
      selector: row => row.total_records,
      sortable: true,
      width: '120px',
      center: true,
      cell: row => (
        <span style={{
          display: 'inline-block',
          border: '1px solid #1976d2',
          color: '#1976d2',
          borderRadius: '16px',
          padding: '2px 12px',
          fontSize: '0.875rem',
          fontWeight: 500,
          background: '#e3f2fd',
          minWidth: 32,
          textAlign: 'center',
        }}>{row.total_records}</span>
      ),
      ignoreRowClick: true
    },
    {
      name: 'Saved Records',
      selector: row => row.saved_records,
      sortable: true,
      width: '130px',
      center: true,
      cell: row => (
        <span style={{
          display: 'inline-block',
          border: '1px solid #2e7d32',
          color: '#2e7d32',
          borderRadius: '16px',
          padding: '2px 12px',
          fontSize: '0.875rem',
          fontWeight: 500,
          background: '#e8f5e9',
          minWidth: 32,
          textAlign: 'center',
        }}>{row.saved_records}</span>
      ),
      ignoreRowClick: true
    },
    {
      name: 'Not Saved',
      selector: row => row.not_saved_records,
      sortable: true,
      width: '120px',
      center: true,
      cell: row => (
        <span style={{
          display: 'inline-block',
          border: '1px solid #d32f2f',
          color: '#d32f2f',
          borderRadius: '16px',
          padding: '2px 12px',
          fontSize: '0.875rem',
          fontWeight: 500,
          background: '#ffebee',
          minWidth: 32,
          textAlign: 'center',
        }}>{row.not_saved_records}</span>
      ),
      ignoreRowClick: true
    },
    {
      name: 'Status',
      sortable: true,
      width: '160px', // Slightly wider for pill and icon
      cell: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{
            display: 'inline-block',
            border: row.is_confirm ? '1px solid #2e7d32' : '1px solid #ed6c02',
            color: row.is_confirm ? '#2e7d32' : '#ed6c02',
            borderRadius: '16px',
            padding: '2px 12px',
            fontSize: '0.875rem',
            fontWeight: 500,
            background: row.is_confirm ? '#e8f5e9' : '#fff3e0',
            minWidth: 70,
            textAlign: 'center',
          }}>{row.is_confirm ? 'Confirmed' : 'Pending'}</span>
          {row.canViewImportBatch && (
            <IconButton size="small" sx={{ ml: 1 }} onClick={() => handleViewBatch(row.reference_number)}>
              <VisibilityIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
    {
      name: 'Actions',
      width: '200px',
      center: true,
      cell: row => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Tooltip title="View Import Batch">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleViewBatch(row.reference_number)}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.is_confirm ? 'Unconfirm' : 'Confirm'}>
            <IconButton
              color={row.is_confirm ? 'warning' : 'success'}
              size="small"
              onClick={() => handleConfirm(row.id, row.is_confirm)}
            >
              {row.is_confirm ? <CancelIcon /> : <CheckCircleIcon />}
            </IconButton>
          </Tooltip>
          {
            !row.is_confirm && (
              <Tooltip title="Delete All Batches">
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  sx={{ minWidth: 180 }}
                  onClick={async () => {
                    const result = await confirmDelete(
                      'Do you want to delete all the batch numbers in this reference number? You could not revert after delete.'
                    );
                    if (result.isConfirmed) {
                      try {
                        const res = await dispatch(deleteImportedInventoryByReference(row.reference_number));
                        toast.success(res?.message || 'Deleted successfully');
                      } catch (err) {
                        const errorMsg = err?.response?.data?.message || err?.message || 'An error occurred while deleting';
                        toast.error(errorMsg);
                      }
                    }
                  }}
                >
                  Delete All Batches
                </Button>
              </Tooltip>
            )
          }
          {user?.role === 'Admin' && (
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                onClick={() => handleDelete(row.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f5f5f5',
        fontWeight: 'bold',
        borderBottom: '2px solid #e0e0e0'
      }
    },
    rows: {
      style: {
        minHeight: '60px',
        '&:hover': {
          backgroundColor: '#f8f9fa'
        }
      }
    },
    cells: {
      style: {
        padding: '12px 8px'
      }
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <DataTable
        title="CSV Import Logs"
        columns={columns}
        data={logs}
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 50]}
        highlightOnHover={false}
        pointerOnHover={false}
        progressPending={loading}
        progressComponent={<CircularProgress />}
        dense
        customStyles={customStyles}
        responsive
        noDataComponent={
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <p>No CSV import logs found</p>
          </Box>
        }
      />
    </Box>
  );
};

export default CsvImportLogsTable; 